import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useQuery } from '@apollo/client'
import { Card, device, Pill, shadow } from '@ticketswap/solar'
import { Ticket } from '@ticketswap/comets'
import getGeoInfo from '~/utils/getGeoInfo'
import { generateImagePlaceholder } from '~/utils'
import { PreviewCard } from '~/components/PreviewCard'
import LittleCalendar from '~/components/LittleCalendar'
import ArtistAvatarCount from '~/components/ArtistAvatarCount'
import { GET_POPULAR_EVENTS } from '~/components/PopularEvents/getPopularEvents'
import { getEventLocation } from '~/utils/eventDescription'
import { Skeleton } from '~/components/ListCard/Skeleton'

const StyledCard = styled(Card)`
  box-shadow: ${shadow.lg};

  :first-of-type {
    transform: translateX(0.25rem) translateY(0.25rem) rotate(2deg);

    @media ${device.tablet} {
      transform: translateX(0.75rem) translateY(-0.5rem) rotate(6deg);
    }
  }

  :last-of-type {
    position: absolute;
    top: 0;
    width: 100%;
    transform: translateY(0.5rem) rotate(-6deg);

    @media ${device.tablet} {
      transform: rotate(-2.5deg);
    }
  }
`

const Preview = styled.div`
  position: relative;
  transform: scale(0.8);
`

const StackedCards = ({ t, title, text, button }) => {
  const geoInfo = getGeoInfo()
  const { latitude, longitude } = geoInfo

  const { data, loading } = useQuery(GET_POPULAR_EVENTS, {
    variables: {
      nearby: {
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        radius: 20,
        fallback: { to: 'COUNTRY', whenCountBelow: 2 },
      },
      first: 2,
    },
    skip: !longitude || !latitude,
  })

  if (loading) {
    return <Skeleton />
  }

  if (!data) return null

  const popularItems = data.activeEvents?.edges || []
  const backgroundImage = popularItems.length !== 0 ? popularItems[0].node.imageUrl : ''

  return (
    <PreviewCard image={backgroundImage} blurred title={title} text={text} button={button}>
      <Preview>
        {popularItems.map(({ node = {} }) => (
          <StyledCard
            key={node.id}
            className="card"
            image={node.imageUrl || generateImagePlaceholder(300, 200)}
            title={node.name}
            leftAdornment={<LittleCalendar startDate={node.startDate} endDate={node.endDate} />}
            rightAdornment={<Pill leftAdornment={<Ticket size={16} />}>25</Pill>}
            topLeftAdornment={
              node.artists?.length > 0 ? (
                <ArtistAvatarCount
                  avatarsList={node.artists}
                  avatarCount={2}
                  totalNumber={node.artists.length}
                  t={t}
                />
              ) : undefined
            }
            text={getEventLocation(node)}
            verticalAlign="top"
          />
        ))}
      </Preview>
    </PreviewCard>
  )
}

StackedCards.propTypes = {
  t: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  button: PropTypes.node,
}

export default StackedCards
